import { Button, Dialog, Flex, Input, ProviderConsumer, Text } from '@fluentui/react-northstar';
import { Communication, TToolbarInteraction } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import '../AppGlobals';
import { MobileSettingsView } from '../automation/AutomationTable';
import ConfigCSS from '../config/config.module.css';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';
import { Breadcrumbs } from "../shared/components/BreadcrumbNavigation";
import { CheckLogin } from '../shared/components/CheckLogin';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { TicketTemplate } from '../shared/interfaces/ticket.interface';
import { platformService } from '../shared/services/platform.service';
import { BUTTONS, CONTACT_ADMIN, LICENSE_FEATURE, REFRESH_SESSION, SAVE, SETTINGS, SOMETHING_WENT_WRONG, UNAUTHORIZED_ACCESS } from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { AdaptiveCardDesigner } from '../templates/AdaptiveCardDesigner';
import { FORMS, NEW_FORM } from '../templates/CustomForms/constants';
import { SetDefault_CustomForm } from '../templates/SetDefault_CustomForm';
import useUnsavedChangesWarning from '../shared/hooks/useUnsavedChangesWarning';

class Form implements Form {
    constructor(id, name, definition, isArchived, isDefault) {
        this.Id = id;
        this.Name = name;
        this.Definition = definition;
        this.IsArchived = isArchived;
        this.IsDefault = isDefault;
    }
    Id: 0;
    Name: any;
    Definition: any;
    IsArchived: false;
    IsDefault: false;
}

export interface IFormProperties {
    id: string
}

export const Forms = (props: IFormProperties) => {
    const { t } = useTranslation();
    const history = useHistory();
    const api = new platformService();

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errCode, setErrCode] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [form, setForm] = useState<Form>(new Form(0, "", "", false, false));
    const [formName, setFormName] = useState("");
    const defaultData = { CardResponseJson: "{}", isCardValid: true } as TicketTemplate;
    const [data, dispatchData] = useState(defaultData);
    const [isFormFeatureEnabled, setIsFormFeatureEnabled] = useState<boolean>(false);

    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [customFormDialogOpen, setCustomFormDialogOpen] = useState<boolean>(false);
    const [selectedCardOption, setSelectedCardOption] = useState("");
    const [selectedCard, setSelectedCard] = useState(null)

    const [isFormUpdated, setIsFormUpdated] = useState<boolean>(false);
    const [Prompt, setDirty] = useUnsavedChangesWarning();
    const setFormDirty: (boolVlaue: boolean) => void = setDirty as (boolVlaue: boolean) => void;

    useEffect(() => {
        setFormDirty(isFormUpdated);
    }, [isFormUpdated]);

    const onToolbarInteraction = (interaction: TToolbarInteraction) => {
        if (interaction.action === "toggle-menu") {
            setMenuOpen(!menuOpen);
        }
    }

    const fetchData = async () => {
        setLoadingMessage(t('forms.loading-your-form'));
        const isFeatureEnabled = await getCachedFeature(LICENSE_FEATURE.CustomForms);
        setIsFormFeatureEnabled(isFeatureEnabled);

        if (props.id && parseInt(props.id) > 0) {
            try {
                const result = await api.getForms(`(${props.id})`)
                const value = result.data
                let formData = new Form(value.Id, value.Name, value.Definition, value.IsArchived, value.IsDefault);

                initFormData(formData);
                setIsError(false);
            } catch (e) {
                setIsError(true);
                setErrCode(e["response"]?.status ?? 500);
            }
        } else {
            setCustomFormDialogOpen(true);
            dispatchData({ CardResponseJson: '{}', isCardValid: true } as TicketTemplate);
            setIsFormUpdated(false);
        }
        setIsLoading(false);
    }


    const initFormData = (formData: Form) => {
        setForm(formData);
        setFormName(formData.Name);
        dispatchData({ CardResponseJson: formData.Definition, IsDefault: formData.IsDefault, isCardValid: true } as TicketTemplate);
        setIsFormUpdated(false);
    }

    const loadPage = () => {
        fetchData();
    }

    const errConfig = {
        fields: {
            title: (errCode == 401) ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
            desc: CONTACT_ADMIN
        }
    };

    const saveData = async () => {
        setLoadingMessage(t('forms.updating-your-forms'));
        setIsLoading(true);

        form.Name = formName;
        form.Definition = data.CardResponseJson;

        if (form.Id === 0) {
            await api.createForm(form);
        } else {
            await api.updateForm(form);
        }

        setIsFormUpdated(false)
        setIsLoading(false);
        history.push(`/settings/forms`);
    };

    const disableSave = () => {
        if (formName != "" && data.isCardValid)
            return false;

        return true;
    }

    const updateData = () => {
        dispatchData({ CardResponseJson: data.CardResponseJson, isCardValid: data.isCardValid } as TicketTemplate);
        setIsFormUpdated(true);
    }

    const updateFormName = event => {
        setFormName(event.target.value);
        setIsFormUpdated(true)
    };

    const navs: Breadcrumbs = {
        breadcrumbs: [
            {
                title: t('common.breadcrumb.settings'),
                link: '/settings'
            },
            {
                title: t('common.breadcrumb.forms'),
                link: '/settings/forms'
            },
            {
                title: (form.Name.length > 0) ? toTitleCase(form.Name) : toTitleCase(NEW_FORM),
                link: ""
            }
        ]
    };

    const renderForm = () => {
        return (isError) ? (<><Communication {...errConfig} />
            {errCode === 401 && (<Button content={REFRESH_SESSION} primary onClick={e => { Providers.globalProvider.login(); }} />)}</>) :
            (<ProviderConsumer render={(globalTheme) => (
                <>
                    {(!isFormFeatureEnabled && !form.IsDefault) && (<UpgradeNeeded
                        toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(FORMS)}`}
                        headerText={t('ticket.custom-form.no-access')}
                        subheaderText={t('ticket.custom-form.upgrade-plan')} />)}
                    {(isFormFeatureEnabled || form.IsDefault) && (<div style={{ display: 'block', ...{ '--input-background-color': 'var(--mgt-theme-background)' }, ...ThemeColorScheme(globalTheme.siteVariables) }}>
                        <Toolbar onInteraction={onToolbarInteraction} globalTheme={globalTheme} title="Settings > Forms" saveText={SAVE} saveCommand={saveData} breadcrumbs={navs} disableSave={disableSave()} />

                        <div className='ml-2 mt-5 mb-5 mr-5'>
                            <div>
                                <Flex styles={{ paddingTop: "20px" }}>
                                    <Text content={t('forms.input-fields.name')} style={{ display: 'block' }} />
                                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                                </Flex>
                            </div>
                            <div className="teams-input-med">
                                <Input autoFocus clearable inverted placeholder={t('forms.input-fields.enter-form-name')} value={formName} onChange={updateFormName} fluid />
                            </div>
                        </div>
                        <Flex className={ConfigCSS.configBody}>
                            <Flex.Item>
                                <Flex fill className={`pr-3 lg:w-1/2 w-full h-`}>
                                    {<AdaptiveCardDesigner templateData={data} dispatchData={updateData} reRender={selectedCard} />}
                                </Flex>
                            </Flex.Item>
                        </Flex>

                    </div>)}
                </>)}
            />)
    }

    const onCancelClick = () => {
        setCustomFormDialogOpen(false);
        setIsLoading(false);
        history.push('/settings/forms');
    }

    const onConfirmClick = () => {
        setCustomFormDialogOpen(false);
        setIsLoading(false);
    }

    const handleSelectedOptionChange = (newValue) => {
        setSelectedCardOption(newValue);
    }

    const handleSelectedCardChange = (newValue) => {
        setSelectedCard(newValue);
    }

    return (
        <CheckLogin onSignedIn={loadPage}>
            <Helmet>
                <title>{((formName?.length ?? 0) > 0) ? formName : NEW_FORM} - Tikit</title>
            </Helmet>
            <div className="hidden md:block">
                {
                    (isLoading) ? <LoadingScreen message={loadingMessage} /> : renderForm()

                }
            </div>
            <MobileSettingsView />
            <Dialog
                content={<SetDefault_CustomForm templateProps={{ templateData: data, dispatchData: dispatchData }} handleSelectedCardChange={handleSelectedCardChange} handleSelectedCardOptionChange={handleSelectedOptionChange} />}
                open={customFormDialogOpen}
                footer={<><Flex gap="gap.small">
                    <Flex.Item push>
                        <Button content={BUTTONS.CANCEL} onClick={onCancelClick} />
                    </Flex.Item>
                    <Flex.Item>
                        <Button content={BUTTONS.CONFIRM} primary disabled={selectedCard == null && selectedCardOption == "default"} onClick={onConfirmClick} />
                    </Flex.Item>
                </Flex>
                </>}
            />
            {Prompt}
        </CheckLogin>
    )
}